import * as React from "react";
import {
  Grid,
  Box,
  Tooltip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from "@mui/material";
import { connect, useDispatch } from "react-redux";
import Typography from "@mui/material/Typography";
import { push } from "connected-react-router";
import EditIcon from "@mui/icons-material/Edit";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import SmsIcon from "@mui/icons-material/Sms";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment-timezone";
import startCase from "lodash/startCase";
import { t, tl } from "../../components/translate";
import { ageFormatter, separateByComma } from "../../helpers/formatters";
import { clientFullNameSelector } from "../../reducers/client";
import Authorize from "../../auth/authorization";
import ActionBar from "../../components/ActionBar/ActionBar";
import { uploadClientImage, Type as ClientActionType } from "../../actions/client";
import { getRCFromState } from "../../actions/resourceCentre";
import { IThunkDispatch, RootState } from "../../store";
import useIsAccountSubscribed from "../../hooks/accounts";
import { convertADtoBS } from "../../components/Calendar/functions/calendarFunctions";
import { MESSAGE_TYPE, MessageType } from "../Campaign/InstantMessage/MessageCreate";
import { Client } from "../../interfaces/ClientInterface";
import { IUser } from "../../interfaces/User";
import ClientImageUploader from "../../components/ImageUploader/ClientImageUploader";
import useAssociateCompanies from "../../hooks/useAssociateCompanies";
import { getCustomerNumber } from "./ClientList";
import { notificationAdd } from "../../actions/notification";
import { sendAppLinkToClient } from "../../api/client";
import { actionCreatorAsync } from "../../actions/actionHelpers";

enum ClientDetailKey {
  CUSTOMER_NUMBER = "customerNumber",
  PHONE = "phone",
  SECONDARY_PHONE = "secondaryPhone",
  EMAIL = "email",
  OPENING_BALANCE = "openingBalance",
  OPENING_BALANCE_DATE = "openingBalanceDate",
  TRANSACTION_TYPE = "transactionType",
  IPD_NO = "ipdNo",
  REGISTRATION_NO = "registrationNo"
}

const commonDetailKey = [
  ClientDetailKey.CUSTOMER_NUMBER,
  ClientDetailKey.PHONE,
  ClientDetailKey.SECONDARY_PHONE,
  ClientDetailKey.EMAIL,
  ClientDetailKey.IPD_NO,
  ClientDetailKey.REGISTRATION_NO
];

const detailKeyForAccountUser = [
  ClientDetailKey.OPENING_BALANCE,
  ClientDetailKey.OPENING_BALANCE_DATE,
  ClientDetailKey.TRANSACTION_TYPE
];

interface ClientShowProps {
  client: Client;
  user: IUser;
  navigateTo: (url: string) => void;
  showActionbar: boolean;
  uploadPhoto: (id: number, image: Blob) => void;
  allowUpload: boolean;
  onMessageBtnClick: (mType: MessageType) => void;
  isInEmergency?: boolean;
}
const ClientShow = ({
  client,
  user,
  navigateTo,
  showActionbar,
  uploadPhoto,
  allowUpload,
  onMessageBtnClick,
  isInEmergency = false
}: ClientShowProps) => {
  const dispatch = useDispatch();
  const companies = useAssociateCompanies();
  const isAccountSubscribed = useIsAccountSubscribed();

  const [loadingSendAppLink, setLoadingSendAppLink] = React.useState(false);

  const age = ageFormatter(client.dob);

  const moreDetailsKeys: (keyof Client)[] = [
    "occupation",
    "maritalStatus",
    "bloodGroup",
    "ethnicity",
    "nationality",
    "appliedCountry",
    "nationalIdentityNumber",
    "panVatNumber",
    "wardNo",
    "internalNotes",
    "insuranceNumber"
  ];

  const hasMoreDetails =
    moreDetailsKeys.some((key) => client[key]) ||
    !!client.passportInfo?.passportNumber ||
    (client.insuranceImages && client.insuranceImages.length > 0);

  let actions: {
    label: string;
    action: () => void;
    Icon: React.ElementType;
    accessKey: string;
  }[] = [];
  if (Authorize(user).can("admin", "Patient", { id: client.id })) {
    actions = actions.concat([
      {
        label: t("clients.edit"),
        action: () => navigateTo(`/clients/${client.id}/edit`),
        Icon: EditIcon,
        accessKey: "clients:editClient"
      }
    ]);
  }

  const handleSendAppLink = async () => {
    try {
      setLoadingSendAppLink(true);
      const { id } = client;
      const { client: clt } = await sendAppLinkToClient(id);

      dispatch(actionCreatorAsync(ClientActionType.CLIENT_UPDATE, () => Promise.resolve(clt)));

      // send app link
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: "App link sent successfully",
          autoTimeout: true
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: err?.message || err?.data?.message || "Error sending app link",
          autoTimeout: true
        })
      );
    } finally {
      setLoadingSendAppLink(false);
    }
  };

  return (
    <>
      {showActionbar && <ActionBar actions={actions} />}

      <Grid>
        <Grid container>
          <Grid size={12}>
            <Box display="flex" alignItems="center">
              <Typography variant="h6"> {clientFullNameSelector(client).toUpperCase()} </Typography>
              {isInEmergency && (
                <Chip
                  label="Emergency"
                  color="error"
                  size="small"
                  sx={{ ml: 1, fontWeight: 600 }}
                />
              )}
              {!client.isCorporateClient && allowUpload && (
                <Box ml="32px">
                  <ClientImageUploader
                    client={client}
                    action={(image) => {
                      uploadPhoto(client.id, image);
                    }}
                    initialImage={client.customerDetails?.profileImageS3Url || ""}
                  />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid size={12}>
            <Typography>
              {age} {client.gender ? tl(`clients.gender.${client.gender}`) : ""}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ width: "100%" }}>
          <Box display="flex">
            <Typography
              sx={{
                width: "25%",
                fontWeight: 600,
                fontSize: "0.875rem",
                order: 1
              }}
            >
              {tl("clients.created_at")}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.875rem",
                order: 2
              }}
            >
              {moment(client.created_at).format("YYYY-MM-DD (hh:mm A)")}
            </Typography>
          </Box>

          {[...commonDetailKey, ...(isAccountSubscribed ? detailKeyForAccountUser : [])].map(
            (attr) =>
              client[attr] ? (
                <Box key={attr} display="flex">
                  <Box order={1} width="25%" fontSize="14px">
                    <Typography style={{ fontWeight: 600 }}>{tl(attr)}</Typography>
                  </Box>
                  <Box order={2} fontSize="14px">
                    {attr === ClientDetailKey.OPENING_BALANCE_DATE ? (
                      <Typography>{convertADtoBS(new Date(client[attr])).formatted4}</Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center"
                        }}
                      >
                        <Typography>
                          {attr === ClientDetailKey.CUSTOMER_NUMBER
                            ? getCustomerNumber(client[attr])
                            : client[attr]}
                        </Typography>
                        {attr === ClientDetailKey.EMAIL && (
                          <Tooltip title="Send Email">
                            <ForwardToInboxIcon
                              sx={{ cursor: "pointer", width: "0.9rem" }}
                              onClick={() => onMessageBtnClick(MESSAGE_TYPE.EMAIL)}
                            />
                          </Tooltip>
                        )}
                        {attr === ClientDetailKey.PHONE && (
                          <Tooltip title="Send SMS">
                            <SmsIcon
                              sx={{ cursor: "pointer", width: "0.9rem" }}
                              onClick={() => onMessageBtnClick(MESSAGE_TYPE.SMS)}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : (
                ""
              )
          )}

          {(client.city || client.address) && (
            <>
              <Box display="flex">
                <Box order={1} width="25%" fontSize="14px">
                  <Typography style={{ fontWeight: 600 }}>{tl("clients.address")}</Typography>
                </Box>
                <Box order={2} fontSize="14px">
                  <Typography>{separateByComma(client.address, client.city)}</Typography>
                </Box>
              </Box>
            </>
          )}

          {client.ssfId && (
            <Box display="flex">
              <Box order={1} width="25%" fontSize="14px">
                <Typography sx={{ fontWeight: 600 }}>SsfId</Typography>
              </Box>
              <Box order={2} fontSize="14px">
                <Typography>{client.ssfId}</Typography>
              </Box>
            </Box>
          )}

          {client.associatedCompanyId && (
            <>
              <Box display="flex">
                <Box order={1} width="25%" fontSize="14px">
                  <Typography style={{ fontWeight: 600 }}>
                    {tl("clients.associatedCompany")}
                  </Typography>
                </Box>
                <Box order={2} fontSize="14px">
                  <Typography>
                    {startCase(
                      companies.find((company) => company.id === client.associatedCompanyId)
                        ?.name || ""
                    )}
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {client?.landline && (
            <Box display="flex">
              <Box order={1} width="25%" fontSize="14px">
                <Typography sx={{ fontWeight: 600 }}>Landline</Typography>
              </Box>
              <Box order={2} fontSize="14px">
                <Typography>{client.landline}</Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography sx={{ width: "25%", fontWeight: 600, fontSize: "14px" }}>
              {tl("clients.appLinkSentStatus")}
            </Typography>
            <Box sx={{ fontSize: "14px" }}>
              {client?.appLinkSentDate
                ? `Sent on ${moment(client.appLinkSentDate).format("YYYY-MM-DD")}`
                : tl("clients.notSentYet")}
            </Box>

            {!client?.appLinkSentDate && client?.phone && (
              <Button
                sx={{
                  marginLeft: "20px"
                }}
                loading={loadingSendAppLink}
                size="small"
                variant="outlined"
                onClick={handleSendAppLink}
              >
                {tl("clients.sendAppLink")}
              </Button>
            )}
          </Box>

          {hasMoreDetails && (
            <Accordion
              elevation={0}
              sx={{
                "&:before": { display: "none" },
                border: "1px solid",
                borderColor: "divider",
                mb: 2
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>More Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {[
                    { key: "occupation", label: tl("CreateClient.Occupation") },
                    { key: "maritalStatus", label: tl("CreateClient.MaritalStatus") },
                    { key: "bloodGroup", label: tl("CreateClient.BLoodGroup") },
                    { key: "ethnicity", label: tl("CreateClient.Ethnicity") },
                    { key: "nationality", label: tl("CreateClient.Nationality") },
                    { key: "appliedCountry", label: tl("CreateClient.AppliedCountry") },
                    { key: "nationalIdentityNumber", label: tl("CreateClient.NIDNo.") },
                    { key: "citizenshipNumber", label: tl("CreateClient.CitizenshipNumber") },
                    { key: "panVatNumber", label: "PAN/VAT Number" },
                    { key: "wardNo", label: "Ward No" },
                    { key: "internalNotes", label: tl("CreateClient.InternalNotes") },
                    { key: "insuranceNumber", label: tl("CreateClient.InsuranceNumber") }
                  ].map(
                    (item) =>
                      client[item.key as keyof Client] && (
                        <Grid size={{ xs: 12, sm: 6 }} key={item.key}>
                          <Box display="flex" flexDirection="column">
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              {item.label}
                            </Typography>
                            <Typography variant="body2">
                              {String(client[item.key as keyof Client])}
                            </Typography>
                          </Box>
                        </Grid>
                      )
                  )}

                  {/* passport info */}
                  {client.passportInfo?.passportNumber && (
                    <Grid size={12}>
                      <Box mt={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Passport Information
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Passport Number
                            </Typography>
                            <Typography variant="body2">
                              {client.passportInfo.passportNumber}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Issuing Authority
                            </Typography>
                            <Typography variant="body2">
                              {client.passportInfo.issuingAuthority}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Issue Date
                            </Typography>
                            <Typography variant="body2">{client.passportInfo.issueDate}</Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Expiry Date
                            </Typography>
                            <Typography variant="body2">
                              {client.passportInfo.expiryDate}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {/* insurance info */}
                  {client.insuranceImages && client.insuranceImages.length > 0 && (
                    <Grid size={12}>
                      <Box mt={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Insurance Images
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {client.insuranceImages.map((img) => (
                            <Box
                              key={img.id}
                              component="img"
                              src={img.s3ResourceURL}
                              sx={{
                                width: 100,
                                height: 100,
                                objectFit: "cover",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                                cursor: "pointer"
                              }}
                              onClick={() => window.open(img.s3ResourceURL, "_blank")}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </Grid>
    </>
  );
};

export default connect(
  (state: RootState) => {
    const rc = getRCFromState(state);
    return {
      user: { ...state.userContext.user, role: state.userContext.mode },
      allowUpload: rc?.settings.clientSettings.enablePictureUpload
    };
  },
  (dispatch: IThunkDispatch) => ({
    navigateTo: (url: string) => dispatch(push(url)),
    uploadPhoto: (id: number, image: Blob) => dispatch(uploadClientImage(id, image))
  })
)(ClientShow);
